/**
 * Application mount — everything that renders the SPA.
 *
 * Imported DYNAMICALLY by main.tsx, only after the runtime config and the active
 * locale's catalog are in memory. Non-default locales are lazy chunks, and many
 * modules evaluate `tr()` copy at import time (option lists, label maps), so the
 * application graph must not be evaluated before the catalog is there — keeping
 * these imports out of the entry module guarantees that ordering. (The build
 * modulepreloads this chunk from index.html, so it downloads in parallel with
 * the entry; only its evaluation waits.)
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { RouterProvider } from 'react-router/dom';

import { getUserManager, onSigninCallback } from '@/auth/oidc';
import { router } from '@/app/router';
import { prefetchAllRoutes } from '@/app/prefetchRoutes';
import { TopErrorBoundary } from '@/app/AppError';
import { installGlobalErrorReporting } from '@/lib/errorReporting';
import { I18nProvider } from '@/i18n';
import { ActingTenantProvider } from '@/app/providers/ActingTenantProvider';
import PwaInstallPrompt from '@/components/PwaInstallPrompt';

/**
 * Register the hand-rolled service worker (public/sw.js) for offline app-shell
 * caching. Production-only so Vite's dev HMR is never intercepted; fully guarded
 * so an unsupporting browser just skips it (progressive enhancement).
 */
function registerServiceWorker() {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;
  const register = () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* SW registration failed — the SPA keeps working without offline cache */
    });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

export function mountApp(): void {
  // Catch + report uncaught errors / unhandled rejections (prod-only email).
  installGlobalErrorReporting();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <TopErrorBoundary>
        <AuthProvider userManager={getUserManager()} onSigninCallback={onSigninCallback}>
          <I18nProvider>
            <ActingTenantProvider>
              <RouterProvider router={router} />
              <PwaInstallPrompt />
            </ActingTenantProvider>
          </I18nProvider>
        </AuthProvider>
      </TopErrorBoundary>
    </React.StrictMode>,
  );

  registerServiceWorker();

  // Warm all lazy route chunks in the background so navigation is instant and the
  // target page's controls are interactive on arrival — closes the cold-chunk
  // window behind the first-click-needs-two-clicks issue (BUG-13).
  prefetchAllRoutes(router.routes as never);
}
